/**
 * One `GetExerciseResponse` from the wire, one row the app can render.
 *
 * Ported from `move-with-me-frontend/.../features/exercises/model/mapExercise.ts` plus the list
 * half of its `exercise.types.ts`. This is the only module in the slice that knows the DTO's shape.
 *
 * One of mobile's fields does not come across, for a stated reason rather than by omission:
 *
 * · **`cover`** — mobile resolves a bundled `ImageSourcePropType` from an asset catalogue this app
 *   has not ported (backend coverage gap report §5.9). The raw `coverImageUrl` is carried instead,
 *   so whichever surface first needs a thumbnail has the URL and no bundling decision is made here.
 *
 * **`equipment` and `measurementFields` joined in PR-B2C**, on the commit that gave each a reader,
 * exactly as the two notes scheduling them said. The rail's `Sprzęt` facet reads the first; the
 * rail's insert reads the second, because `createExercise` seeds a fresh exercise's columns from
 * the movement's measurement contract and a pick with no contract would insert a column-less
 * exercise. The parser moved to `shared/measurement` in the same round, which is what makes the
 * second one reachable from here at all — entities may not import each other.
 *
 * Name and description arrive ALREADY RESOLVED into the reader's language — the fetcher sends
 * `lang` — so they are carried through untouched and never run through i18n. That is why every read
 * of this endpoint must carry a `lang`: a blank one resolves to English rather than erroring, so an
 * omission is silent and the trainer simply sees English exercise names.
 */

import { measurementFieldsFromWire, type MeasurementFieldKey } from '~/shared/measurement';
import type {
  TrainWithMeModulesExercisesDtosResponsesEquipmentResponse,
  TrainWithMeModulesExercisesDtosResponsesExerciseAliasResponse,
  TrainWithMeModulesExercisesDtosResponsesExerciseTagResponse,
  TrainWithMeModulesExercisesDtosResponsesGetExerciseResponse,
} from '~/shared/api/generated/model';

import { exerciseTypeFromApi, type ExerciseTypeKey } from './exerciseType';
import { muscleGroupToApi, muscleGroupsFromApi, type MuscleGroup } from './muscleGroup';

type ApiExercise = TrainWithMeModulesExercisesDtosResponsesGetExerciseResponse;
type ApiEquipment = TrainWithMeModulesExercisesDtosResponsesEquipmentResponse;
type ApiAlias = TrainWithMeModulesExercisesDtosResponsesExerciseAliasResponse;
type ApiTag = TrainWithMeModulesExercisesDtosResponsesExerciseTagResponse;

/**
 * One piece of equipment a movement needs.
 *
 * The NAME is backend data, already localised, so the facet chip prints it rather than looking a
 * label up — the taxonomy is open (a trainer's custom exercise can reference anything the catalogue
 * holds) and an i18n table over it would go stale the first time the backend added a row.
 */
export interface ExerciseEquipment {
  readonly id: number;
  /** Backend DATA, resolved into the reader's language. Never i18n. */
  readonly name: string;
  /**
   * Absolute url of the piece's own photograph, or `null`.
   *
   * The backend ships these in `SeedAssets/equipment` beside the exercise covers, so they behave
   * the same way after a deploy as they do locally. Every one of the 24 pieces the catalogue names
   * carries one today; the type still admits `null`, because a piece a trainer's custom exercise
   * references need not have a picture and a surface that assumed one would draw a broken image.
   */
  readonly imageUrl: string | null;
}

/**
 * The longest name a movement may carry: `PostExerciseRequest.Name` is `[MaxLength(200)]`. The
 * creator's field stops at it, and a name written elsewhere that is longer can only reach the
 * catalogue through the creator, where the trainer shortens it.
 */
export const MAX_EXERCISE_NAME_LENGTH = 200;

/** The library row. */
export interface ExerciseListItem {
  readonly id: number;
  /** Backend DATA, resolved into the reader's language. Never i18n. */
  readonly name: string;
  /** `null` when the backend's value is missing or unknown to this build. */
  readonly typeKey: ExerciseTypeKey | null;
  readonly primaryMuscles: readonly MuscleGroup[];
  readonly secondaryMuscles: readonly MuscleGroup[];
  /** What this movement measures, in the dictionary's one canonical order. May be EMPTY. */
  readonly measurementFields: readonly MeasurementFieldKey[];
  /** Every piece of equipment the movement names. Empty for a bodyweight movement. */
  readonly equipment: readonly ExerciseEquipment[];
  /** Absolute CDN url of the cover still, or `null`. */
  readonly coverImageUrl: string | null;
  /**
   * Absolute url of the demo video's poster frame, or `null`.
   *
   * A SECOND photograph of the same movement, and much the more common one: measured against the
   * local catalogue on 9 September 2026, 385 of the 477 movements name a poster and 82 name a
   * cover, and only 66 name both. `stillUrl` is what a surface should read — see it for why the two
   * are not interchangeable in the other direction.
   */
  readonly posterUrl: string | null;
  /** True when the exercise belongs to a trainer rather than the shared catalogue. */
  readonly isCustom: boolean;
  /**
   * Added from Buddy's proposal and not yet marked as checked by its owner (exercise-library R17):
   * „Do uzupełnienia". Absent from an older server's answer, which reads as checked.
   */
  readonly needsReview: boolean;
  /**
   * Every other name the library knows this movement by, in EVERY language (R18).
   *
   * All languages and not only the one on screen, because the search matches all of them exactly as
   * the server does (R4) — a Polish trainer browsing English content still finds „wykrok bułgarski".
   * Only the theatre lists them, and only in the content language (R20); a row never draws one.
   * Empty for a custom exercise and for an older backend that does not send the field.
   */
  readonly aliases: readonly ExerciseAlias[];
  /**
   * The signed-in trainer's own tags on this movement, as they spell them (R21). Never anybody
   * else's — the server attaches the caller's alone. Empty where there are none, and for an older
   * backend that does not send the field.
   */
  readonly tags: readonly string[];
}

/** One alias: a name, and the content language it belongs to. */
export interface ExerciseAlias {
  /** A plain string on the wire, so a third content language changes no type here. */
  readonly lang: string;
  /** Backend DATA. Never i18n. */
  readonly name: string;
}

/**
 * Equipment rows the wire sent without an id or without a name are dropped.
 *
 * The id is the facet's identity — two nameless rows would collide into one chip — and a chip with
 * no name is a chip nobody can read. Neither is recoverable here, so the honest answer is that the
 * movement names one fewer piece of equipment than the backend thinks it does.
 */
export function toEquipment(api: readonly ApiEquipment[]): ExerciseEquipment[] {
  return api
    .filter((item) => (item.id ?? 0) > 0 && (item.name ?? '').trim().length > 0)
    .map((item) => ({
      id: item.id ?? 0,
      name: (item.name ?? '').trim(),
      // Blank is the same answer as absent: an empty string in `src` resolves to the current
      // document and makes the browser fetch the page into an `<img>`.
      imageUrl: (item.imageUrl ?? '').trim().length > 0 ? (item.imageUrl ?? null) : null,
    }));
}

/**
 * Aliases with a blank name or language are dropped: a nameless alias finds nothing, and one with
 * no language cannot be listed in any.
 */
function toAliases(api: readonly ApiAlias[] | null | undefined): ExerciseAlias[] {
  return (api ?? []).flatMap((alias) => {
    const name = (alias.name ?? '').trim();
    const lang = (alias.lang ?? '').trim().toLowerCase();
    return name === '' || lang === '' ? [] : [{ lang, name }];
  });
}

/** A tag with a blank label is no tag. The wire carries objects so a later field is additive. */
function toTagLabels(api: readonly ApiTag[] | null | undefined): string[] {
  return (api ?? []).flatMap((tag) => {
    const label = (tag.label ?? '').trim();
    return label === '' ? [] : [label];
  });
}

/**
 * The names a movement's detail lists under its own (R20): its aliases in the content language
 * being read, in the library's order. Another language's aliases still FIND it in the search and
 * are not listed; a language with none lists nothing.
 */
export function aliasesIn(item: ExerciseListItem, lang: string): readonly string[] {
  return item.aliases.filter((alias) => alias.lang === lang).map((alias) => alias.name);
}

/** Project one wire item. */
export function toExerciseListItem(api: ApiExercise): ExerciseListItem {
  return {
    id: api.id ?? 0,
    name: api.name ?? '',
    typeKey: exerciseTypeFromApi(api.exerciseType),
    primaryMuscles: muscleGroupsFromApi(api.primaryMuscleGroups),
    secondaryMuscles: muscleGroupsFromApi(api.secondaryMuscleGroups),
    // Through the one entry point that also SORTS, so a row's contract reaches the set table in the
    // dictionary's order and no surface re-orders it (R4).
    measurementFields: measurementFieldsFromWire(api.measurementFields),
    equipment: toEquipment(api.equipment ?? []),
    coverImageUrl: api.coverImageUrl ?? null,
    posterUrl: api.posterUrl ?? null,
    isCustom: api.personalTrainerId != null,
    needsReview: api.needsReview === true,
    aliases: toAliases(api.aliases),
    tags: toTagLabels(api.tags),
  };
}

/**
 * The one photograph of a movement a surface should draw, or `null` when there is none.
 *
 * **Poster first, cover second, and that order is measured rather than preferred.** The catalogue
 * names a poster for 385 of its 477 movements and a cover for 82; 66 name both and none of those 66
 * agree on the url, so neither field is a superset of the other and reading only one leaves real
 * photographs on the wire. Taking both, 397 of the 477 answer with a still that resolves — against
 * 76 for the cover alone, which is the number the library rows were drawn against when the tile
 * fallback was added on 9 September 2026 and is why most of them drew a tile.
 *
 * Poster leads because it is the frame of the movement being PERFORMED, which is what a trainer is
 * identifying; the cover is a catalogue picture. A surface still needs the tile fallback either way
 * — 80 movements have neither, and four of the posters the catalogue names 404 against the media
 * root.
 */
export function stillUrl(item: ExerciseListItem): string | null {
  return item.posterUrl ?? item.coverImageUrl;
}

/**
 * The same two photographs, taken the other way round — for a surface that draws one LARGE.
 *
 * The poster is a video frame and the seed ships it at 720x402; a cover is an uploaded still at
 * 1376x768 (both measured off the running media root, 18 September 2026). At the table's 44x33
 * thumb the difference is invisible and the poster's advantage in coverage decides it, which is
 * `stillUrl` above. The theatre's hero measures 636 css px at the 1440 desk and ~679 where the
 * panel reaches its 1240 cap — about 1270 to 1360 device px on the 2x display this app is read on,
 * where a 720px file is upscaled near enough twice and reads soft while the cover is not upscaled
 * at all. So the hero asks for the cover first and falls back to the poster, and no surface holds
 * that preference privately.
 *
 * It does not make every hero sharp. Measured against the catalogue on 18 September 2026: of 479
 * movements, 77 name a cover and get one, 330 name a poster and no cover and keep the 720px file,
 * and 72 name neither and draw the tile. The soft 330 are a CONTENT task — the posters have to be
 * re-rendered at 1440px or wider — and it is recorded in the canon's deviations register.
 */
export function heroStillUrl(item: ExerciseListItem): string | null {
  return item.coverImageUrl ?? item.posterUrl;
}

/**
 * The slice's read entry point: the whole response in, rows out.
 *
 * Rows the backend sent without an id are dropped rather than rendered with `id: 0`. A row keyed on
 * zero would collide with every other such row in React's reconciler, and — more to the point here
 * — it would collide in the attribution map below, so one nameless exercise would silently supply
 * the muscle groups for every other.
 */
export function toExerciseListItems(
  items: readonly ApiExercise[] | undefined,
): ExerciseListItem[] {
  if (items === undefined) return [];
  return items.filter((item) => (item.id ?? 0) > 0).map(toExerciseListItem);
}

/**
 * What one exercise trains, in the backend's own PascalCase group names.
 *
 * The names rather than this slice's slugs, deliberately. The consumer is
 * `entities/plan-draft`'s muscle rollup, and **entities of the same layer may not import each
 * other** (execution ADR §2) — so the two cannot share a type, and the wire's vocabulary is the one
 * thing they are both already defined against. Structural typing does the rest: the page hands this
 * map straight to `muscleLoadByDay`, and the compiler checks the shapes line up without either
 * slice knowing the other exists.
 *
 * Round-tripping through `muscleGroupsFromApi` and back through `muscleGroupToApi` rather than
 * passing the raw strings is what canonicalises the casing and drops a group this build has never
 * heard of — at the one seam that is the authority on what a muscle group is.
 */
export interface ExerciseMuscleAttribution {
  readonly primary: readonly string[];
  readonly secondary: readonly string[];
}

const toWireNames = (groups: readonly MuscleGroup[]): string[] => groups.map(muscleGroupToApi);

/**
 * The attribution map the muscle rollup takes as its parameter: exercise id → what it trains.
 *
 * An exercise the read did not return is simply absent, and the rollup treats absence as "no
 * contribution" rather than as zero — so a library read that failed produces bars that are missing
 * rather than bars that are wrong.
 */
export function toMuscleAttribution(
  items: readonly ExerciseListItem[],
): ReadonlyMap<number, ExerciseMuscleAttribution> {
  return new Map(
    items.map((item) => [
      item.id,
      { primary: toWireNames(item.primaryMuscles), secondary: toWireNames(item.secondaryMuscles) },
    ]),
  );
}
